import React, { forwardRef, useState } from 'react';
import { Input } from './input';
import { Eye, EyeSlash } from '@phosphor-icons/react';

/**
 * PasswordInput — drop-in replacement for `<Input type="password" />` with a
 * built-in show/hide eye toggle. Preserves every prop (name, value, onChange,
 * placeholder, required, data-testid, disabled, className, autoComplete, ...).
 *
 * Usage:
 *   <PasswordInput value={pwd} onChange={e => setPwd(e.target.value)} placeholder="Password" data-testid="login-password" />
 */
export const PasswordInput = forwardRef(function PasswordInput(
  { className = '', 'data-testid': testId, ...props },
  ref,
) {
  const [visible, setVisible] = useState(false);
  const toggleTestId = testId ? `${testId}-toggle` : 'password-visibility-toggle';
  return (
    <div className="relative">
      <Input
        {...props}
        ref={ref}
        type={visible ? 'text' : 'password'}
        data-testid={testId}
        className={`${className} pr-10`}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setVisible((v) => !v)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
        aria-label={visible ? 'Hide password' : 'Show password'}
        title={visible ? 'Hide password' : 'Show password'}
        data-testid={toggleTestId}
        disabled={props.disabled}
      >
        {visible ? <EyeSlash size={16} weight="regular" /> : <Eye size={16} weight="regular" />}
      </button>
    </div>
  );
});

export default PasswordInput;
